import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _objectDestructuringEmpty from "@babel/runtime/helpers/objectDestructuringEmpty";
var _Info;
var _excluded = ["className"],
  _excluded2 = ["className"],
  _excluded3 = ["className"],
  _excluded4 = ["className", "children", "variant"];
import * as React from 'react';
import Info from '@crehana/compass.icons.outline/Info';
import { cn } from '@crehana/compass.ui/v2';
import { Slot } from '@radix-ui/react-slot';
import { Controller, FormProvider, useFormContext } from 'react-hook-form-v7';
import { Label } from "./label";

/** @deprecated Use FormProvider of @crehana/forms/rhf, and Field of @crehana/compass.ui/v4: https://docs-frontend.creha.co/libraries/compass-ui/v4/form/ */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var Form = FormProvider;
var FormFieldContext = /*#__PURE__*/React.createContext({});

/** @deprecated Use Field of @crehana/compass.ui/v4 in a native form with React Hook Form: https://docs-frontend.creha.co/libraries/compass-ui/v4/form/ and https://docs-frontend.creha.co/libraries/compass-ui/v4/field/ */
var FormField = function FormField(_ref) {
  var props = Object.assign({}, (_objectDestructuringEmpty(_ref), _ref));
  return /*#__PURE__*/_jsx(FormFieldContext.Provider, {
    value: {
      name: props.name
    },
    children: /*#__PURE__*/_jsx(Controller, _objectSpread({}, props))
  });
};

/** @deprecated Use Field of @crehana/compass.ui/v4 in a native form with React Hook Form: https://docs-frontend.creha.co/libraries/compass-ui/v4/form/ and https://docs-frontend.creha.co/libraries/compass-ui/v4/field/ */
var useFormField = function useFormField() {
  var fieldContext = React.useContext(FormFieldContext);
  var itemContext = React.useContext(FormItemContext);
  var _useFormContext = useFormContext(),
    getFieldState = _useFormContext.getFieldState,
    formState = _useFormContext.formState;
  var fieldState = getFieldState(fieldContext.name, formState);
  if (!fieldContext) {
    throw new Error('useFormField should be used within <FormField>');
  }
  var id = itemContext.id;
  return _objectSpread({
    id: id,
    name: fieldContext.name,
    formItemId: "".concat(id, "-form-item"),
    formDescriptionId: "".concat(id, "-form-item-description"),
    formMessageId: "".concat(id, "-form-item-message")
  }, fieldState);
};
var FormItemContext = /*#__PURE__*/React.createContext({});

/** @deprecated Use Field of @crehana/compass.ui/v4 in a native form with React Hook Form: https://docs-frontend.creha.co/libraries/compass-ui/v4/form/ and https://docs-frontend.creha.co/libraries/compass-ui/v4/field/ */
var FormItem = /*#__PURE__*/React.forwardRef(function (_ref2, ref) {
  var className = _ref2.className,
    props = _objectWithoutProperties(_ref2, _excluded);
  var id = React.useId();
  return /*#__PURE__*/_jsx(FormItemContext.Provider, {
    value: {
      id: id
    },
    children: /*#__PURE__*/_jsx("div", _objectSpread({
      ref: ref,
      className: cn('tw-space-y-0.8', className)
    }, props))
  });
});
FormItem.displayName = 'FormItem';

/** @deprecated Use Field of @crehana/compass.ui/v4 in a native form with React Hook Form: https://docs-frontend.creha.co/libraries/compass-ui/v4/form/ and https://docs-frontend.creha.co/libraries/compass-ui/v4/field/ */
var FormLabel = /*#__PURE__*/React.forwardRef(function (_ref3, ref) {
  var className = _ref3.className,
    props = _objectWithoutProperties(_ref3, _excluded2);
  var _useFormField = useFormField(),
    error = _useFormField.error,
    formItemId = _useFormField.formItemId;
  return /*#__PURE__*/_jsx(Label, _objectSpread({
    ref: ref,
    className: cn('tw-font-subtitle5', error && 'tw-text-alert-400 dark:tw-text-alert-300', className),
    htmlFor: formItemId
  }, props));
});
FormLabel.displayName = 'FormLabel';

/** @deprecated Use Field of @crehana/compass.ui/v4 in a native form with React Hook Form: https://docs-frontend.creha.co/libraries/compass-ui/v4/form/ and https://docs-frontend.creha.co/libraries/compass-ui/v4/field/ */
var FormControl = /*#__PURE__*/React.forwardRef(function (_ref4, ref) {
  var props = Object.assign({}, (_objectDestructuringEmpty(_ref4), _ref4));
  var _useFormField2 = useFormField(),
    error = _useFormField2.error,
    formItemId = _useFormField2.formItemId,
    formDescriptionId = _useFormField2.formDescriptionId,
    formMessageId = _useFormField2.formMessageId;
  return /*#__PURE__*/_jsx(Slot, _objectSpread({
    ref: ref,
    id: formItemId,
    "aria-describedby": !error ? "".concat(formDescriptionId) : "".concat(formDescriptionId, " ").concat(formMessageId),
    "aria-invalid": !!error
  }, props));
});
FormControl.displayName = 'FormControl';

/** @deprecated Use Field of @crehana/compass.ui/v4 in a native form with React Hook Form: https://docs-frontend.creha.co/libraries/compass-ui/v4/form/ and https://docs-frontend.creha.co/libraries/compass-ui/v4/field/ */
var FormDescription = /*#__PURE__*/React.forwardRef(function (_ref5, ref) {
  var className = _ref5.className,
    props = _objectWithoutProperties(_ref5, _excluded3);
  var _useFormField3 = useFormField(),
    formDescriptionId = _useFormField3.formDescriptionId;
  return /*#__PURE__*/_jsx("p", _objectSpread({
    ref: ref,
    id: formDescriptionId,
    className: cn('tw-font-body5 tw-text-neutral-light-700 dark:tw-text-neutral-dark-400', className)
  }, props));
});
FormDescription.displayName = 'FormDescription';

/** @deprecated Use Field of @crehana/compass.ui/v4 in a native form with React Hook Form: https://docs-frontend.creha.co/libraries/compass-ui/v4/form/ and https://docs-frontend.creha.co/libraries/compass-ui/v4/field/ */
var FormMessage = /*#__PURE__*/React.forwardRef(function (_ref6, ref) {
  var className = _ref6.className,
    children = _ref6.children,
    variant = _ref6.variant,
    props = _objectWithoutProperties(_ref6, _excluded4);
  var _useFormField4 = useFormField(),
    error = _useFormField4.error,
    formMessageId = _useFormField4.formMessageId;
  var body = error ? String(error === null || error === void 0 ? void 0 : error.message) : children;
  if (!body) {
    return null;
  }

  // biome-ignore lint/style/useBlockStatements: single-line early return with JSX
  if (variant === 'colored') return /*#__PURE__*/_jsxs("p", _objectSpread(_objectSpread({
    ref: ref,
    id: formMessageId,
    className: cn('tw-font-body5 tw-py-0.8 tw-px-1.2 tw-text-on-hold-400 tw-bg-on-hold-100 tw-flex tw-items-center tw-gap-0.8 tw-border tw-border-solid tw-border-on-hold-200 tw-rounded-0.8', className)
  }, props), {}, {
    children: [_Info || (_Info = /*#__PURE__*/_jsx(Info, {
      className: "tw-text-inherit",
      size: 16
    })), /*#__PURE__*/_jsx("span", {
      className: "tw-line-clamp-1",
      children: body
    })]
  }));
  return /*#__PURE__*/_jsx("p", _objectSpread(_objectSpread({
    ref: ref,
    id: formMessageId,
    className: cn('tw-font-caption tw-text-alert-400 dark:tw-text-alert-300', className)
  }, props), {}, {
    children: body
  }));
});
FormMessage.displayName = 'FormMessage';
export { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage, useFormField };